import { useState } from "react";
import { useStock } from "../App";
import { db } from "../firebase";
import { collection, addDoc, doc, updateDoc, deleteDoc, query, where, getDocs } from "firebase/firestore";

const Icons = {
  Edit: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>,
  Trash: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path></svg>,
  Slip: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2Z"></path><path d="M8 7h8"></path><path d="M8 11h8"></path><path d="M8 15h5"></path></svg>,
};

const money = (amount) => `¥${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CustomersPage() {
  const { customers, sales } = useStock();

  const [search, setSearch]     = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [slipModal, setSlipModal] = useState(null);
  const [slipDate, setSlipDate] = useState("All");
  const [formData, setFormData] = useState({ name: "" }); // Address removed
  const [editData, setEditData] = useState({ name: "" }); // Address removed
  const [loading, setLoading]   = useState(false);

  // --- Dynamic helpers computed from sales ---
  const normalize = (str) => (str || "").trim().toLowerCase();

  // 1. Total Spent (Strictly from April 1st, 2026 to Now)
  const getTotalSpent = (customerName) => {
    const aprilFirst = new Date("2026-04-01");
    
    return sales
      .filter(s => {
        if (s.status !== "Completed") return false;
        if (normalize(s.customer) !== normalize(customerName)) return false;
        const sDate = new Date(s.date);
        return sDate >= aprilFirst;
      })
      .reduce((sum, s) => sum + (parseFloat(s.amountNum) || 0), 0);
  };

  // 2. Last 7 Days Purchases
  const getLast7DaysSpent = (customerName) => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return sales
      .filter(s => {
        if (s.status !== "Completed") return false;
        if (normalize(s.customer) !== normalize(customerName)) return false;
        const sDate = new Date(s.date);
        return sDate >= sevenDaysAgo;
      })
      .reduce((sum, s) => sum + (parseFloat(s.amountNum) || 0), 0);
  };

  // 3. Unpaid Total
  const getUnpaidTotal = (customerName) =>
    sales
      .filter(s => s.status === "Unpaid" && normalize(s.customer) === normalize(customerName))
      .reduce((sum, s) => sum + (parseFloat(s.amountNum) || 0), 0);

  const getUnpaidSales = (customerName) =>
    sales
      .filter(s => s.status === "Unpaid" && normalize(s.customer) === normalize(customerName))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

  const openSlip = (customer) => {
    setSlipModal(customer);
    setSlipDate("All");
  };

  const handlePrintSlip = () => {
    const printContent = document.getElementById("unpaid-slip-print-area")?.innerHTML;
    if (!printContent) return;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // 4. Last Purchase Date
  const getLastPurchaseDate = (customerName) => {
    const customerSales = sales
      .filter(s => (s.status === "Completed" || s.status === "Unpaid") && normalize(s.customer) === normalize(customerName))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return customerSales.length > 0 ? customerSales[0].date : null;
  };

  const filtered = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  // --- Add ---
  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    const newCustomer = {
      id: `CUS-${String(customers.length + 1).padStart(3, "0")}`,
      ...formData,
      createdAt: new Date(),
    };
    try {
      await addDoc(collection(db, "customers"), newCustomer);
      setShowModal(false);
      setFormData({ name: "" });
    } catch (err) {
      alert("Failed to add customer: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Edit ---
  const openEdit = (c) => {
    setEditModal(c);
    setEditData({ name: c.name });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const q = query(collection(db, "customers"), where("id", "==", editModal.id));
      const snap = await getDocs(q);
      if (snap.empty) throw new Error("Customer not found in database.");
      await updateDoc(doc(db, "customers", snap.docs[0].id), {
        name: editData.name,
      });
      setEditModal(null);
    } catch (err) {
      alert("Update failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Delete ---
  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete "${customer.name}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      const q = query(collection(db, "customers"), where("id", "==", customer.id));
      const snap = await getDocs(q);
      if (snap.empty) throw new Error("Customer not found.");
      await deleteDoc(doc(db, "customers", snap.docs[0].id));
    } catch (err) {
      alert("Delete failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #e2e8f0", outline: "none", fontSize: 14, boxSizing: "border-box", background: "#f8fafc" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, padding: "10px 0" }}>

      <div style={{ background: "white", borderRadius: 16, border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
          <div>
            <p style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.5px" }}>Customer Directory</p>
            <p style={{ fontSize: 13, color: "#94a3b8", marginTop: 4, marginBottom: 14 }}>{filtered.length} customers found</p>
            <button onClick={() => setShowModal(true)} style={{ background: "#6366f1", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 600, boxShadow: "0 4px 6px -1px rgba(99,102,241,0.2)" }}>
              + Add Customer
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 16px" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers..." style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#0f172a", width: 140, fontWeight: 500 }} />
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "750px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                {/* Notice Address is removed, Last 7 Days Added */}
                {["Customer", "Total Spent (Since Apr)", "Last 7 Days", "Total Unpaid", "Last Purchase", "Actions"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 600, padding: "0 8px 12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const totalSpent   = getTotalSpent(c.name);
                const last7Days    = getLast7DaysSpent(c.name);
                const unpaid       = getUnpaidTotal(c.name);
                const lastPurchase = getLastPurchaseDate(c.name);
                
                return (
                  <tr key={c.id} style={{ borderBottom: "1px solid #f8fafc" }} onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                    
                    <td style={{ padding: "16px 8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "white", flexShrink: 0 }}>
                          {c.name.charAt(0)}
                        </div>
                        <div>
                          <p style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", margin: 0 }}>{c.name}</p>
                          <p style={{ fontSize: 12, color: "#94a3b8", margin: "2px 0 0 0", fontFamily: "monospace" }}>{c.id}</p>
                        </div>
                      </div>
                    </td>
                    
                    <td style={{ padding: "16px 8px", fontSize: 14, color: "#0f172a", fontWeight: 700 }}>
                      {totalSpent > 0 ? `¥${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}
                    </td>

                    <td style={{ padding: "16px 8px", fontSize: 14, color: "#10b981", fontWeight: 700 }}>
                      {last7Days > 0 ? `¥${last7Days.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}
                    </td>

                    <td style={{ padding: "16px 8px" }}>
                      {unpaid > 0 ? (
                        <span style={{ background: "#fee2e2", color: "#b91c1c", fontSize: 13, fontWeight: 700, padding: "4px 10px", borderRadius: 6 }}>
                          ¥{unpaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span style={{ fontSize: 13, color: "#94a3b8" }}>—</span>
                      )}
                    </td>

                    <td style={{ padding: "16px 8px", fontSize: 13, color: "#64748b", fontWeight: 500 }}>
                      {lastPurchase || "—"}
                    </td>

                    <td style={{ padding: "16px 8px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => openEdit(c)} style={{ display: "flex", alignItems: "center", gap: 5, background: "#eef2ff", color: "#6366f1", border: "1px solid #c7d2fe", padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                          {Icons.Edit} Edit
                        </button>
                        <button
                          onClick={() => unpaid > 0 ? openSlip(c) : alert("No unpaid bills found for this customer.")}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            background: unpaid > 0 ? "#fff7ed" : "#f8fafc",
                            color: unpaid > 0 ? "#c2410c" : "#94a3b8",
                            border: unpaid > 0 ? "1px solid #fed7aa" : "1px solid #e2e8f0",
                            padding: "6px 10px",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600
                          }}
                        >
                          {Icons.Slip} Slip
                        </button>
                        <button onClick={() => handleDelete(c)} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 5, background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca", padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                          {Icons.Trash} Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {customers.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <p style={{ fontSize: 15, color: "#94a3b8", margin: 0, fontWeight: 500 }}>No customers added yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* --- UNPAID SLIP MODAL --- */}
      {slipModal && (() => {
        const unpaidSales = getUnpaidSales(slipModal.name);
        const dateOptions = Array.from(new Set(unpaidSales.map((sale) => sale.date))).sort((a, b) => new Date(b) - new Date(a));
        const visibleSales = slipDate === "All" ? unpaidSales : unpaidSales.filter((sale) => sale.date === slipDate);
        const totalDue = visibleSales.reduce((sum, sale) => sum + (parseFloat(sale.amountNum) || 0), 0);

        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
            <div style={{ background: "white", borderRadius: 20, width: "100%", maxWidth: 620, maxHeight: "90vh", overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <h3 style={{ margin: 0, color: "#0f172a", fontSize: 20, fontWeight: 800 }}>Unpaid Bill Slip</h3>
                  <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>{slipModal.name}</p>
                </div>
                <select value={slipDate} onChange={(e) => setSlipDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#f8fafc", color: "#0f172a", fontWeight: 700, outline: "none" }}>
                  <option value="All">All Dates</option>
                  {dateOptions.map((date) => <option key={date} value={date}>{date}</option>)}
                </select>
              </div>

              <div style={{ padding: 24, overflowY: "auto" }}>
                <div id="unpaid-slip-print-area" style={{ background: "white", color: "#111827", fontFamily: "system-ui, sans-serif" }}>
                  <div style={{ textAlign: "center", borderBottom: "1px dashed #cbd5e1", paddingBottom: 14, marginBottom: 16 }}>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: "0.5px" }}>FRESH FARM CORNER</h2>
                    <p style={{ margin: "5px 0 0", fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 800 }}>Unpaid Bill Slip</p>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16, fontSize: 13 }}>
                    <div><strong>Customer:</strong> {slipModal.name}</div>
                    <div style={{ textAlign: "right" }}><strong>Date Filter:</strong> {slipDate === "All" ? "All Dates" : slipDate}</div>
                    <div><strong>Generated:</strong> {new Date().toLocaleDateString("en-US")}</div>
                    <div style={{ textAlign: "right" }}><strong>Total Due:</strong> {money(totalDue)}</div>
                  </div>

                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid #cbd5e1", color: "#475569", textTransform: "uppercase" }}>
                        {["Date", "Sale ID", "Items", "Amount"].map((head) => (
                          <th key={head} style={{ textAlign: head === "Amount" ? "right" : "left", padding: "8px 6px", fontSize: 11 }}>{head}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSales.length === 0 ? (
                        <tr><td colSpan="4" style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>No unpaid bills for this date.</td></tr>
                      ) : visibleSales.map((sale) => (
                        <tr key={sale.docId || sale.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "10px 6px", fontWeight: 700 }}>{sale.date}</td>
                          <td style={{ padding: "10px 6px", color: "#6366f1", fontWeight: 800 }}>{sale.id}</td>
                          <td style={{ padding: "10px 6px", color: "#334155" }}>{sale.itemsSummary}</td>
                          <td style={{ padding: "10px 6px", textAlign: "right", fontWeight: 900 }}>{money(sale.amountNum)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ borderTop: "1px dashed #cbd5e1", marginTop: 16, paddingTop: 14, display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 900 }}>
                    <span>Total Unpaid</span>
                    <span>{money(totalDue)}</span>
                  </div>
                </div>
              </div>

              <div style={{ padding: "16px 24px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", gap: 12 }}>
                <button onClick={() => setSlipModal(null)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid #cbd5e1", background: "white", color: "#64748b", fontWeight: 800, cursor: "pointer" }}>Close</button>
                <button onClick={handlePrintSlip} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#c2410c", color: "white", fontWeight: 800, cursor: "pointer" }}>Print Slip</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* --- ADD MODAL --- */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: "white", padding: 32, borderRadius: 20, width: "100%", maxWidth: 400 }}>
            <h3 style={{ margin: "0 0 24px", color: "#0f172a", fontSize: 20, fontWeight: 700 }}>Add New Customer</h3>
            <form onSubmit={handleAdd} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Name <span style={{ color: "#ef4444" }}>*</span></label>
                <input required placeholder="Customer Full Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "white", color: "#64748b", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#4f46e5", color: "white", fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- EDIT MODAL --- */}
      {editModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: "white", padding: 32, borderRadius: 20, width: "100%", maxWidth: 400 }}>
            <h3 style={{ margin: "0 0 4px", color: "#0f172a", fontSize: 20, fontWeight: 700 }}>Edit Customer</h3>
            <p style={{ margin: "0 0 24px", fontSize: 12, color: "#94a3b8", fontFamily: "monospace" }}>{editModal.id}</p>
            <form onSubmit={handleEdit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Name <span style={{ color: "#ef4444" }}>*</span></label>
                <input required value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button type="button" onClick={() => setEditModal(null)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "white", color: "#64748b", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#4f46e5", color: "white", fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
